import type { ActivityAdapter } from "@/app/integration/contracts";
import {
  focusSocialTargetSearchAdapter as reviewedFocusSocialTargetSearchAdapter,
  type FocusSocialTargetSearchAction,
  type FocusSocialTargetSearchObjectModel as ReviewedTargetSearchObjectModel,
  type FocusSocialTargetSearchRenderModel as ReviewedTargetSearchRenderModel,
  type FocusSocialTargetSearchRoundModel as ReviewedTargetSearchRoundModel
} from "@/features/domain-slices/focus-social/target-search-adapter";
import type {
  TargetSearchPayload,
  TargetSearchState
} from "@/game/templates/search";

export type {
  FocusSocialTargetSearchAction,
  FocusSocialTargetSearchFeedbackModel,
  FocusSocialTargetSearchResultModel,
  FocusSocialTargetSearchShape,
  FocusSocialTargetSearchSwatch
} from "@/features/domain-slices/focus-social/target-search-adapter";

export type FocusSocialTargetSearchVisualKind =
  | "backpack"
  | "ball"
  | "boot"
  | "button"
  | "crayon"
  | "cup"
  | "flower"
  | "gift"
  | "hat"
  | "kite"
  | "leaf"
  | "pencil"
  | "pencil-case"
  | "ruler"
  | "sock"
  | "umbrella";

export type FocusSocialTargetSearchVisualColor =
  | "blue"
  | "green"
  | "purple"
  | "red"
  | "yellow";

export type FocusSocialTargetSearchVisualPattern =
  | "dot"
  | "four-hole"
  | "plain"
  | "star"
  | "stripe"
  | "two-hole";

export type FocusSocialTargetSearchVisualScale =
  | "large"
  | "long"
  | "short"
  | "small"
  | "standard";

export type FocusSocialTargetSearchVisualForm =
  | "pointed"
  | "round"
  | "square"
  | "standard";

export type FocusSocialTargetSearchVisualDirection =
  | "left"
  | "none"
  | "right";

export type FocusSocialTargetSearchVisualCount =
  | "none"
  | "one"
  | "three"
  | "two";

export interface FocusSocialTargetSearchAppearance {
  readonly appearanceKey: string;
  readonly kind: FocusSocialTargetSearchVisualKind;
  readonly primary: FocusSocialTargetSearchVisualColor;
  readonly accent: FocusSocialTargetSearchVisualColor | "none";
  readonly pattern: FocusSocialTargetSearchVisualPattern;
  readonly scale: FocusSocialTargetSearchVisualScale;
  readonly form: FocusSocialTargetSearchVisualForm;
  readonly direction: FocusSocialTargetSearchVisualDirection;
  readonly count: FocusSocialTargetSearchVisualCount;
}

export interface FocusSocialTargetSearchObjectModel
  extends Omit<ReviewedTargetSearchObjectModel, "shape" | "swatch"> {
  readonly appearance: FocusSocialTargetSearchAppearance;
}

export interface FocusSocialTargetSearchRoundModel
  extends Omit<ReviewedTargetSearchRoundModel, "objects"> {
  readonly objects: readonly FocusSocialTargetSearchObjectModel[];
}

export interface FocusSocialTargetSearchRenderModel
  extends Omit<ReviewedTargetSearchRenderModel, "round"> {
  readonly round: FocusSocialTargetSearchRoundModel | null;
}

type AppearanceOptions = Partial<
  Pick<
    FocusSocialTargetSearchAppearance,
    "accent" | "pattern" | "scale" | "form" | "direction" | "count"
  >
>;

function defineAppearance(
  kind: FocusSocialTargetSearchVisualKind,
  primary: FocusSocialTargetSearchVisualColor,
  options: AppearanceOptions = {}
): FocusSocialTargetSearchAppearance {
  const appearance = {
    kind,
    primary,
    accent: options.accent ?? "none",
    pattern: options.pattern ?? "plain",
    scale: options.scale ?? "standard",
    form: options.form ?? "standard",
    direction: options.direction ?? "none",
    count: options.count ?? "none"
  } as const;

  return {
    ...appearance,
    appearanceKey: [
      appearance.kind,
      appearance.primary,
      appearance.accent,
      appearance.pattern,
      appearance.scale,
      appearance.form,
      appearance.direction,
      appearance.count
    ].join(":")
  };
}

const TARGET_SEARCH_OBJECT_APPEARANCES: Readonly<
  Record<string, FocusSocialTargetSearchAppearance>
> = {
  "blue-cup": defineAppearance("cup", "blue"),
  "red-ball": defineAppearance("ball", "red"),
  "yellow-kite": defineAppearance("kite", "yellow"),
  "green-leaf": defineAppearance("leaf", "green"),

  "yellow-umbrella": defineAppearance("umbrella", "yellow"),
  "red-umbrella": defineAppearance("umbrella", "red"),
  "blue-umbrella": defineAppearance("umbrella", "blue"),
  "yellow-hat": defineAppearance("hat", "yellow"),
  "yellow-rain-boot": defineAppearance("boot", "yellow"),

  "red-star-backpack": defineAppearance("backpack", "red", {
    pattern: "star"
  }),
  "blue-star-backpack": defineAppearance("backpack", "blue", {
    pattern: "star"
  }),
  "red-dot-backpack": defineAppearance("backpack", "red", {
    pattern: "dot"
  }),
  "blue-stripe-backpack": defineAppearance("backpack", "blue", {
    pattern: "stripe"
  }),
  "red-star-pencil-case": defineAppearance("pencil-case", "red", {
    pattern: "star"
  }),
  "blue-star-hat": defineAppearance("hat", "blue", {
    pattern: "star"
  }),

  "small-green-leaf": defineAppearance("leaf", "green", {
    scale: "small"
  }),
  "large-green-leaf": defineAppearance("leaf", "green", {
    scale: "large"
  }),
  "small-yellow-leaf": defineAppearance("leaf", "yellow", {
    scale: "small"
  }),
  "small-red-leaf": defineAppearance("leaf", "red", {
    scale: "small"
  }),
  "small-green-flower": defineAppearance("flower", "green", {
    scale: "small"
  }),
  "small-green-kite": defineAppearance("kite", "green", {
    scale: "small"
  }),
  "long-blue-pencil": defineAppearance("pencil", "blue", {
    scale: "long"
  }),
  "short-blue-pencil": defineAppearance("pencil", "blue", {
    scale: "short"
  }),
  "long-red-pencil": defineAppearance("pencil", "red", {
    scale: "long"
  }),
  "long-green-pencil": defineAppearance("pencil", "green", {
    scale: "long"
  }),
  "long-blue-crayon": defineAppearance("crayon", "blue", {
    scale: "long"
  }),
  "long-blue-ruler": defineAppearance("ruler", "blue", {
    scale: "long"
  }),

  "round-red-two-hole-button": defineAppearance("button", "red", {
    pattern: "two-hole",
    form: "round"
  }),
  "square-red-two-hole-button": defineAppearance("button", "red", {
    pattern: "two-hole",
    form: "square"
  }),
  "round-red-four-hole-button": defineAppearance("button", "red", {
    pattern: "four-hole",
    form: "round"
  }),
  "square-red-four-hole-button": defineAppearance("button", "red", {
    pattern: "four-hole",
    form: "square"
  }),
  "round-blue-two-hole-button": defineAppearance("button", "blue", {
    pattern: "two-hole",
    form: "round"
  }),
  "square-green-two-hole-button": defineAppearance("button", "green", {
    pattern: "two-hole",
    form: "square"
  }),
  "round-yellow-four-hole-button": defineAppearance("button", "yellow", {
    pattern: "four-hole",
    form: "round"
  }),
  "square-blue-four-hole-button": defineAppearance("button", "blue", {
    pattern: "four-hole",
    form: "square"
  }),
  "blue-stripe-yellow-heel-sock": defineAppearance("sock", "blue", {
    accent: "yellow",
    pattern: "stripe"
  }),
  "green-stripe-yellow-heel-sock": defineAppearance("sock", "green", {
    accent: "yellow",
    pattern: "stripe"
  }),
  "blue-dot-yellow-heel-sock": defineAppearance("sock", "blue", {
    accent: "yellow",
    pattern: "dot"
  }),
  "green-dot-yellow-heel-sock": defineAppearance("sock", "green", {
    accent: "yellow",
    pattern: "dot"
  }),
  "blue-stripe-red-heel-sock": defineAppearance("sock", "blue", {
    accent: "red",
    pattern: "stripe"
  }),
  "green-stripe-blue-heel-sock": defineAppearance("sock", "green", {
    accent: "blue",
    pattern: "stripe"
  }),
  "yellow-star-red-heel-sock": defineAppearance("sock", "yellow", {
    accent: "red",
    pattern: "star"
  }),
  "red-dot-blue-heel-sock": defineAppearance("sock", "red", {
    accent: "blue",
    pattern: "dot"
  }),

  "red-dot-right-cup": defineAppearance("cup", "red", {
    pattern: "dot",
    direction: "right"
  }),
  "blue-dot-right-cup": defineAppearance("cup", "blue", {
    pattern: "dot",
    direction: "right"
  }),
  "red-dot-left-cup": defineAppearance("cup", "red", {
    pattern: "dot",
    direction: "left"
  }),
  "blue-dot-left-cup": defineAppearance("cup", "blue", {
    pattern: "dot",
    direction: "left"
  }),
  "red-stripe-right-cup": defineAppearance("cup", "red", {
    pattern: "stripe",
    direction: "right"
  }),
  "blue-star-right-cup": defineAppearance("cup", "blue", {
    pattern: "star",
    direction: "right"
  }),
  "green-dot-left-cup": defineAppearance("cup", "green", {
    pattern: "dot",
    direction: "left"
  }),
  "yellow-stripe-left-cup": defineAppearance("cup", "yellow", {
    pattern: "stripe",
    direction: "left"
  }),
  "round-purple-two-leaf-flower": defineAppearance("flower", "purple", {
    form: "round",
    count: "two"
  }),
  "point-purple-two-leaf-flower": defineAppearance("flower", "purple", {
    form: "pointed",
    count: "two"
  }),
  "round-purple-one-leaf-flower": defineAppearance("flower", "purple", {
    form: "round",
    count: "one"
  }),
  "point-purple-three-leaf-flower": defineAppearance("flower", "purple", {
    form: "pointed",
    count: "three"
  }),
  "round-red-two-leaf-flower": defineAppearance("flower", "red", {
    form: "round",
    count: "two"
  }),
  "point-yellow-two-leaf-flower": defineAppearance("flower", "yellow", {
    form: "pointed",
    count: "two"
  }),
  "round-blue-one-leaf-flower": defineAppearance("flower", "blue", {
    form: "round",
    count: "one"
  }),
  "point-green-three-leaf-flower": defineAppearance("flower", "green", {
    form: "pointed",
    count: "three"
  }),
  "red-box-blue-bow-square-lid": defineAppearance("gift", "red", {
    accent: "blue",
    form: "square"
  }),
  "green-box-blue-bow-square-lid": defineAppearance("gift", "green", {
    accent: "blue",
    form: "square"
  }),
  "red-box-yellow-bow-square-lid": defineAppearance("gift", "red", {
    accent: "yellow",
    form: "square"
  }),
  "green-box-red-bow-square-lid": defineAppearance("gift", "green", {
    accent: "red",
    form: "square"
  }),
  "red-box-blue-bow-round-lid": defineAppearance("gift", "red", {
    accent: "blue",
    form: "round"
  }),
  "green-box-blue-bow-round-lid": defineAppearance("gift", "green", {
    accent: "blue",
    form: "round"
  }),
  "yellow-box-green-bow-round-lid": defineAppearance("gift", "yellow", {
    accent: "green",
    form: "round"
  }),
  "blue-box-red-bow-round-lid": defineAppearance("gift", "blue", {
    accent: "red",
    form: "round"
  })
};

export function getFocusSocialTargetSearchAppearance(
  objectId: string
): FocusSocialTargetSearchAppearance {
  const appearance = TARGET_SEARCH_OBJECT_APPEARANCES[objectId];

  if (appearance === undefined) {
    throw new Error(
      `Target-search object "${objectId}" has no player appearance.`
    );
  }

  return appearance;
}

function toPlayerRenderModel(
  model: ReviewedTargetSearchRenderModel
): FocusSocialTargetSearchRenderModel {
  return {
    ...model,
    round:
      model.round === null
        ? null
        : {
            ...model.round,
            objects: model.round.objects.map((object) => ({
              id: object.id,
              label: object.label,
              appearance: getFocusSocialTargetSearchAppearance(object.id),
              isAvailable: object.isAvailable,
              isFocused: object.isFocused,
              isFound: object.isFound,
              isHinted: object.isHinted
            }))
          }
  };
}

export const focusSocialTargetSearchAdapter: ActivityAdapter<
  TargetSearchPayload,
  TargetSearchState,
  FocusSocialTargetSearchAction,
  FocusSocialTargetSearchRenderModel
> = {
  activityType: reviewedFocusSocialTargetSearchAdapter.activityType,
  parsePayload: reviewedFocusSocialTargetSearchAdapter.parsePayload,
  createInitialState:
    reviewedFocusSocialTargetSearchAdapter.createInitialState,
  reduce: reviewedFocusSocialTargetSearchAdapter.reduce,
  getMetrics: reviewedFocusSocialTargetSearchAdapter.getMetrics,
  toRenderModel: (state, payload) =>
    toPlayerRenderModel(
      reviewedFocusSocialTargetSearchAdapter.toRenderModel(state, payload)
    )
};
